/** One of these, and only ever moving forward. Ported from Entities/Runtime/InterviewSession.cs. */
export const InterviewStatuses = {
    Pending: 'Pending',
    InProgress: 'InProgress',
    /** Candidate finished; the post-completion pipeline is still running. */
    Processing: 'Processing',
    Completed: 'Completed',
    Expired: 'Expired',
    Cancelled: 'Cancelled'
} as const;

/** Surveys and listening sessions are the same mechanism; only the intent and the reports differ. */
export const InteractionKinds = {
    Survey: 'Survey',
    Listening: 'Listening'
} as const;

/** The three sections of the question bank. Interviews are the kind that predates the others. */
export const QuestionKinds = {
    Interview: 'Interview',
    ...InteractionKinds
} as const;

export const ALL_QUESTION_KINDS: readonly string[] = Object.values(QuestionKinds);

/** A questionnaire, or the spoken conversation the module started life as. */
export const SurveyTypes = {
    Form: 'Form',
    General: 'General'
} as const;

/** What a spoken answer is captured as. Video carries its own audio, so there is no third option. */
export const MediaTypes = {
    Audio: 'Audio',
    Video: 'Video'
} as const;

/**
 * How one question is answered, chosen by the admin and enforced by the server. A Form survey uses
 * all four; a General survey and a listening session use only the two media types.
 */
export const ResponseTypes = {
    Text: 'Text',
    Checklist: 'Checklist',
    ...MediaTypes
} as const;

export const ALL_RESPONSE_TYPES: readonly string[] = Object.values(ResponseTypes);
export const ALL_MEDIA_TYPES: readonly string[] = Object.values(MediaTypes);

/** The two response types that record something, and so need consent and storage. */
export const isMediaResponse = (responseType: string): boolean =>
    responseType === ResponseTypes.Audio || responseType === ResponseTypes.Video;

/**
 * No Processing and no Cancelled: nothing is scored afterwards, and a campaign is closed by
 * closing the format rather than by cancelling each response.
 */
export const InteractionStatuses = {
    Pending: 'Pending',
    InProgress: 'InProgress',
    Completed: 'Completed',
    Expired: 'Expired'
} as const;

export const ReviewStatuses = {
    Pending: 'Pending',
    Approved: 'Approved',
    Rejected: 'Rejected'
} as const;

export const IntegrityVerdicts = {
    Clean: 'Clean',
    Minor: 'Minor',
    Suspicious: 'Suspicious',
    HighRisk: 'HighRisk'
} as const;

export const CandidateSources = {
    Manual: 'Manual',
    Csv: 'Csv',
    Api: 'Api'
} as const;

export const AuditActions = {
    Created: 'Created',
    Updated: 'Updated',
    Deleted: 'Deleted',
    Approved: 'Approved',
    Rejected: 'Rejected',
    LoggedIn: 'LoggedIn',
    LoginFailed: 'LoginFailed',
    LoggedOut: 'LoggedOut',
    PasswordChanged: 'PasswordChanged',
    SettingsChanged: 'SettingsChanged',
    /** Recorded on every recording access - the most sensitive read in the product. */
    RecordingViewed: 'RecordingViewed',
    InterviewLinkIssued: 'InterviewLinkIssued'
} as const;

export const WebhookEvents = {
    InterviewCreated: 'interview.created',
    InterviewCompleted: 'interview.completed',
    InterviewReviewed: 'interview.reviewed'
} as const;

export const ALL_WEBHOOK_EVENTS: readonly string[] = Object.values(WebhookEvents);

/**
 * How an invitation reaches somebody. Three channels, one send: a campaign that goes out by email
 * and text is one action to the person running it, not two.
 */
export const MessageChannels = {
    Email: 'Email',
    Whatsapp: 'Whatsapp',
    Sms: 'Sms'
} as const;

export const ALL_MESSAGE_CHANNELS: readonly string[] = Object.values(MessageChannels);

/**
 * Which link went in the message.
 *
 * `Employee` mints a personal link and can therefore say who has not replied. `Shared` sends the
 * format's public URL, which is cheaper to send and anonymous by consequence - the employee types
 * their own code, and nobody can be chased.
 */
export const LinkTypes = {
    Employee: 'Employee',
    Shared: 'Shared'
} as const;

/**
 * A message's fate. Sent means the provider accepted it, which is not the same as delivered - no
 * provider tells us the difference at the moment of sending, and claiming otherwise on screen would
 * be a lie a recruiter acts on.
 */
export const MessageStatuses = {
    Pending: 'Pending',
    Sent: 'Sent',
    Failed: 'Failed'
} as const;
